import React, { useMemo } from 'react'
import moment from 'moment'
import { Grid, GridCell, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import uiConstants from 'config/constants/ui'
import { initiativeUrl } from 'config/constants/urls'
import EventListItemCard from 'src/sites/kits/Events/components/ListItemCard'
import EventListItemRow from 'src/sites/kits/Events/components/ListItemRow'
import InitiativeList from 'src/sites/kits/Initiative/components/List'
import { Title } from 'src/sites/kits/UI'
import { useOrganization } from 'src/core/graphql/hooks'
import { useGlobalState } from 'src/sites/state'

const EventListBlock = ({
   initiativeTypeId,
   paddingLeft,
   paddingRight,
   parentInitiativeId,
   titlebarLeftDetail,
   titlebarRightDetail,
   titleSize,
   viewMode,
   viewModeGridNumColumns
}) => {
   const { theme } = useGlobalState()

   // View Mode - Grid/List View
   const gridColumns = viewMode === uiConstants.viewModes.ROW ? '1' : viewModeGridNumColumns
   const gridSpacing = viewMode === uiConstants.viewModes.GRID ? '30px[a-d] 40px[e-f]' : '0px'
   const ListItemComponent = viewMode === uiConstants.viewModes.GRID
      ? EventListItemCard
      : EventListItemRow

   const { organization } = useOrganization()

   // If there is a initiativeTypeId param specified, show only events of that type
   // Otherwise, we assume it is a full Events app and show all events of
   // any initiative type
   const initiativeTypes = useMemo(() => {
      if (initiativeTypeId) {
         const initiativeType = organization.initiativeTypes
            .find(type => type.id === initiativeTypeId)

         if (initiativeType) {
            return [initiativeType]
         }
      }

      return organization.initiativeTypes
         .filter(type => type.enabled && type.class === 'event')
   }, [organization.initiativeTypes, initiativeTypeId])

   const initiativeTypeIds = initiativeTypes.map(type => type.id)
   const appTitle = initiativeTypeId && initiativeTypes[0]
      ? initiativeTypes[0].namePlural
      : 'Events'

   // Only refire date filters every minute to prevent unecessary re-renders
   const nowRoundedDown = useMemo(() => (
      moment().startOf('minute').toISOString()
   ), [(new Date()).getMinutes()])

   const upcomingEventsFilter = useMemo(() => ({
      archived: 'exclude',
      drafts: 'include',
      levels: 'all',
      parentInitiativeId: parentInitiativeId || undefined,
      sortBy: 'startDateAsc',
      typeIds: initiativeTypeIds,
      limit: 30,
      startDateAfter: nowRoundedDown,
      withAncestors: true
   }), [initiativeTypeIds, nowRoundedDown])

   const pastEventsFilter = useMemo(() => ({
      archived: 'exclude',
      drafts: 'include',
      levels: 'all',
      parentInitiativeId: parentInitiativeId || undefined,
      sortBy: 'startDateDesc',
      typeIds: initiativeTypeIds,
      limit: 30,
      startDateBefore: nowRoundedDown,
      withAncestors: true
   }), [initiativeTypeIds, nowRoundedDown])

   return (
      <>
         <View
            paddingLeft={paddingLeft}
            paddingRight={paddingRight}
            marginTop="40px"
            marginBottom={viewMode === uiConstants.viewModes.ROW && '40px'}>
            <Grid columns={gridColumns} spacing={gridSpacing}>
               <InitiativeList
                  emptyContent={(
                     <GridCell colspan={gridColumns}>
                        <View
                           width="100%"
                           padding={viewMode === uiConstants.viewModes.GRID
                              ? '10px 0px 90px 0px'
                              : '30px 0px 90px 0px'
                           }>
                           <Text size="2" color="#888">
                              {`There are no upcoming ${appTitle} to show`}
                           </Text>
                        </View>
                     </GridCell>
                  )}
                  filter={upcomingEventsFilter}>
                  {initiativeList => initiativeList.items.map((initiative) => {
                     // Note: We kinda assume right now there is only one level of nesting here
                     const parentInitiative = initiative.ancestors[0]
                     const attendeeRole = initiative.roles.find(r => r.type === 'primary')
                     const displayParentInitiativeMeta = parentInitiative &&
                        parentInitiative.class !== 'page'

                     return (
                        <GridCell key={initiative.id}>
                           <ListItemComponent
                              borderColor={theme.tmpContentLineColor}
                              coverMedia={initiative.coverMedia}
                              date={initiative.startDateTimeISO8601}
                              linkTo={initiativeUrl(initiative)}
                              location={initiative.location}
                              name={initiative.name}
                              numAttendees={attendeeRole.numActiveUsers}
                              parentInitiativeName={displayParentInitiativeMeta
                                 ? parentInitiative.name
                                 : undefined}
                              parentInitiativePrivacy={displayParentInitiativeMeta
                                 ? parentInitiative.privacy
                                 : undefined}
                              privacy={initiative.privacy}
                              subtitle={initiative.subtitle}
                              summary={initiative.body.summary}
                              startDate={initiative.startDate}
                              startTime={initiative.startTime}
                              textColor={theme.tmpNavTextColor}
                              textSize={theme.tmpBodyTextSize}
                              virtual={initiative.eventIsVirtual}
                           />
                        </GridCell>
                     )
                  })}
               </InitiativeList>
            </Grid>
         </View>
         <View paddingLeft={paddingLeft} paddingRight={paddingRight}>
            <View
               display="flex"
               alignItems="center"
               height="60px"
               borderTop={viewMode === uiConstants.viewModes.GRID &&
                  `1px solid ${theme.tmpContentLineColor}`
               }
               marginBottom={viewMode === uiConstants.viewModes.GRID &&
                  '20px[a-d] 30px[e-f]'
               }>
               <Title size="sm" color={theme.tmpContentTitleColor}>
                  {`Past ${appTitle}`}
               </Title>
            </View>
            <Grid columns={gridColumns} spacing={gridSpacing}>
               <InitiativeList
                  emptyContent={(
                     <GridCell colspan={gridColumns}>
                        <View
                           width="100%"
                           padding={viewMode === uiConstants.viewModes.GRID
                              ? '10px 0px 50px 0px'
                              : '30px 0px 50px 0px'
                           }>
                           <Text size="2" color="#888">No events to show</Text>
                        </View>
                     </GridCell>
                  )}
                  filter={pastEventsFilter}>
                  {initiativeList => initiativeList.items.map((initiative) => {
                     // Note: We kinda assume right now there is only one level of nesting here
                     const parentInitiative = initiative.ancestors[0]
                     const attendeeRole = initiative.roles.find(r => r.type === 'primary')
                     const displayParentInitiativeMeta = parentInitiative &&
                        parentInitiative.class !== 'page'

                     return (
                        <GridCell key={initiative.id}>
                           <ListItemComponent
                              borderColor={theme.tmpContentLineColor}
                              coverMedia={initiative.coverMedia}
                              date={initiative.startDateTimeISO8601}
                              linkTo={initiativeUrl(initiative)}
                              location={initiative.location}
                              name={initiative.name}
                              numAttendees={attendeeRole.numActiveUsers}
                              parentInitiativeName={displayParentInitiativeMeta
                                 ? parentInitiative.name
                                 : undefined}
                              parentInitiativePrivacy={displayParentInitiativeMeta
                                 ? parentInitiative.privacy
                                 : undefined}
                              privacy={initiative.privacy}
                              subtitle={initiative.subtitle}
                              summary={initiative.body.summary}
                              startDate={initiative.startDate}
                              startTime={initiative.startTime}
                              textColor={theme.tmpNavTextColor}
                              textSize={theme.tmpBodyTextSize}
                              virtual={initiative.eventIsVirtual}
                              pastEvent
                           />
                        </GridCell>
                     )
                  })}
               </InitiativeList>
            </Grid>
         </View>
      </>
   )
}

EventListBlock.propTypes = {
   paddingLeft: PropTypes.string,
   paddingRight: PropTypes.string,
   parentInitiativeId: PropTypes.string,
   titleSize: PropTypes.string,
   titlebarLeftDetail: PropTypes.node,
   titlebarRightDetail: PropTypes.node,
   viewMode: PropTypes.oneOf([
      uiConstants.viewModes.GRID,
      uiConstants.viewModes.ROW
   ]),
   viewModeGridNumColumns: PropTypes.string
}

EventListBlock.defaultProps = {
   paddingLeft: undefined,
   paddingRight: undefined,
   parentInitiativeId: undefined,
   titleSize: 'md',
   titlebarLeftDetail: undefined,
   titlebarRightDetail: undefined,
   viewMode: uiConstants.viewModes.GRID,
   viewModeGridNumColumns: '1[a] 2[b-f]'
}

export default EventListBlock
